import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { ArrowLeft, CreditCard, Lock } from 'lucide-react';
import {
  CONTENT_MAX_WIDE,
  CONTENT_INSET,
  pageTopPadding,
} from '../styles/layout';
import {
  GUIDES_PATH,
  getGuideProductById,
  formatGuidePrice,
} from '../constants/guides';
import stripePromise, {
  STRIPE_APPEARANCE,
  STRIPE_PAYMENT_ELEMENT_OPTIONS,
} from '../config/stripe';
import {
  createGuidePaymentIntent,
  updateGuideCheckoutCustomer,
} from '../services/guideCheckout';

const GOLD = '#C9A84C';
const BG = '#090909';
const CARD_BG = '#111111';
const INPUT_BG = '#141414';
const TEXT = '#F5F0E8';
const MUTED = 'rgba(245, 240, 232, 0.72)';

const labelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: MUTED,
  marginBottom: '0.45rem',
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: INPUT_BG,
  color: TEXT,
  border: '1px solid #4b5563',
  borderRadius: '0.75rem',
  padding: '0.85rem 1rem',
  fontSize: '1rem',
  outline: 'none',
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

function GuidePaymentForm({
  product,
  amountUsd,
  fullName,
  email,
  setFullName,
  setEmail,
  paymentIntentId,
  clientSecret,
  onSuccess,
  isMobile,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const submittedRef = useRef(false);

  const handleElementLoadError = (event) => {
    const message =
      event?.error?.message ||
      'Unable to load the card form. Please refresh and try again.';
    console.error('Stripe PaymentElement load error:', event?.error);
    setError(message);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittedRef.current || processing) return;
    if (!stripe || !elements) return;

    const name = fullName.trim();
    const mail = email.trim().toLowerCase();

    if (name.length < 2) {
      setError('Please enter your full name.');
      return;
    }
    if (!isValidEmail(mail)) {
      setError('Please enter a valid email for delivery.');
      return;
    }
    if (!termsAccepted) {
      setError('Please agree to the Terms of Service before paying.');
      return;
    }
    if (!clientSecret?.startsWith('pi_') || !paymentIntentId) {
      setError('Payment session is invalid. Refresh and try again.');
      return;
    }

    submittedRef.current = true;
    setProcessing(true);
    setError('');

    try {
      await updateGuideCheckoutCustomer(paymentIntentId, {
        fullName: name,
        email: mail,
      });

      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name,
              email: mail,
            },
          },
          return_url: `${window.location.origin}${GUIDES_PATH}?checkout=success`,
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message || 'Payment failed. Please try again.');
      }

      if (paymentIntent?.status === 'succeeded') {
        onSuccess(paymentIntent);
        return;
      }

      if (paymentIntent?.status === 'requires_action') {
        submittedRef.current = false;
        setProcessing(false);
        return;
      }

      throw new Error(`Payment status: ${paymentIntent?.status || 'unknown'}. Please try again.`);
    } catch (err) {
      submittedRef.current = false;
      setError(err?.message || 'Unable to process payment.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="dd-stripe-form">
      <div style={{ marginBottom: '0.9rem' }}>
        <label style={labelStyle} htmlFor="guide-checkout-name">
          Full name
        </label>
        <input
          id="guide-checkout-name"
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          style={inputStyle}
          autoComplete="name"
          required
        />
      </div>
      <div style={{ marginBottom: '1.15rem' }}>
        <label style={labelStyle} htmlFor="guide-checkout-email">
          Email for delivery
        </label>
        <input
          id="guide-checkout-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
          autoComplete="email"
          required
        />
      </div>

      <div
        className="dd-stripe-element"
        style={{
          padding: isMobile ? '1rem' : '1.15rem',
          border: '1px solid rgba(201, 168, 76, 0.25)',
          borderRadius: '0.75rem',
          backgroundColor: INPUT_BG,
          marginBottom: '1rem',
        }}
      >
        <PaymentElement
          options={STRIPE_PAYMENT_ELEMENT_OPTIONS}
          onLoadError={handleElementLoadError}
        />
      </div>

      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.55rem',
          color: TEXT,
          fontSize: '0.875rem',
          marginBottom: '1rem',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          required
          style={{ marginTop: '0.2rem' }}
        />
        <span>
          I agree to the{' '}
          <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: GOLD }}>
            Terms of Service
          </Link>
        </span>
      </label>

      {error ? (
        <p role="alert" style={{ margin: '0 0 0.85rem', color: '#fca5a5', fontSize: '0.875rem' }}>
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={!stripe || processing || !termsAccepted}
        style={{
          width: '100%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          padding: '0.95rem 1.25rem',
          borderRadius: '10px',
          border: 'none',
          backgroundColor: processing ? '#4b5563' : GOLD,
          color: '#0A0A0A',
          fontWeight: 800,
          fontSize: '0.9rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          cursor: processing ? 'wait' : 'pointer',
        }}
      >
        {processing ? (
          'Processing…'
        ) : (
          <>
            Pay {formatGuidePrice(amountUsd ?? product.priceUsd)}
            <CreditCard size={18} aria-hidden="true" />
          </>
        )}
      </button>

      <p
        style={{
          margin: '0.85rem 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.35rem',
          color: '#9ca3af',
          fontSize: '0.75rem',
        }}
      >
        <Lock size={12} aria-hidden="true" />
        Your payment information is secure and encrypted
      </p>
    </form>
  );
}

const GuideCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(false);

  const productIdFromState = location.state?.productId;
  const productIdFromQuery = searchParams.get('product');
  const productId = productIdFromState || productIdFromQuery || '';
  const product = getGuideProductById(productId);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [amountUsd, setAmountUsd] = useState(null);
  const [discountUsd, setDiscountUsd] = useState(0);
  const [appliedPromo, setAppliedPromo] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [loadingIntent, setLoadingIntent] = useState(true);
  const [initError, setInitError] = useState('');
  const createdKeyRef = useRef('');

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!product) {
      navigate(GUIDES_PATH, { replace: true });
    }
  }, [product, navigate]);

  const applyCheckoutSession = (data, promoCode) => {
    setClientSecret(data.clientSecret);
    setPaymentIntentId(data.paymentIntentId);
    setAmountUsd(data.amountUsd);
    setDiscountUsd(Number(data.discountUsd) || 0);
    setAppliedPromo(data.promoCode || '');
    createdKeyRef.current = `${product.id}:${promoCode || ''}`;
  };

  useEffect(() => {
    if (!product) return undefined;
    const key = `${product.id}:`;
    if (createdKeyRef.current === key && clientSecret) return undefined;

    let cancelled = false;
    const init = async () => {
      setLoadingIntent(true);
      setInitError('');
      setPromoError('');
      try {
        const data = await createGuidePaymentIntent(product.id);
        if (cancelled) return;
        applyCheckoutSession(data, '');
        setPromoInput('');
      } catch (err) {
        if (cancelled) return;
        setInitError(err?.message || 'Unable to load payment form.');
      } finally {
        if (!cancelled) setLoadingIntent(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const handleApplyPromo = async () => {
    if (!product || promoBusy) return;
    const code = promoInput.trim();
    if (!code) {
      setPromoError('Enter a promo code.');
      return;
    }

    setPromoBusy(true);
    setPromoError('');
    try {
      const data = await createGuidePaymentIntent(product.id, null, code);
      applyCheckoutSession(data, data.promoCode || code.toUpperCase());
    } catch (err) {
      setPromoError(err?.message || 'Invalid promo code.');
    } finally {
      setPromoBusy(false);
    }
  };

  const handleRemovePromo = async () => {
    if (!product || promoBusy) return;
    setPromoBusy(true);
    setPromoError('');
    try {
      const data = await createGuidePaymentIntent(product.id);
      applyCheckoutSession(data, '');
      setPromoInput('');
    } catch (err) {
      setPromoError(err?.message || 'Unable to remove promo code.');
    } finally {
      setPromoBusy(false);
    }
  };

  if (!product) return null;

  const handlePaid = (paymentIntent) => {
    navigate(
      `${GUIDES_PATH}?checkout=success&payment_intent=${encodeURIComponent(paymentIntent.id)}`,
      { replace: true }
    );
  };

  const container = {
    maxWidth: CONTENT_MAX_WIDE,
    margin: '0 auto',
    padding: `0 ${CONTENT_INSET}`,
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div
      className="dd-page dd-guide-checkout"
      style={{
        backgroundColor: BG,
        color: TEXT,
        paddingTop: pageTopPadding(isMobile),
        paddingBottom: isMobile ? '3rem' : '4.5rem',
        minHeight: '70vh',
      }}
    >
      <Helmet>
        <title>Checkout · {product.title} | Digital Divot Golf</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div style={container}>
        <Link
          to={GUIDES_PATH}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            color: MUTED,
            textDecoration: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            marginBottom: isMobile ? '1.25rem' : '1.75rem',
          }}
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Back to Guides
        </Link>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '0.95fr 1.05fr',
            gap: isMobile ? '1.25rem' : '2rem',
            alignItems: 'start',
          }}
        >
          <aside
            style={{
              backgroundColor: CARD_BG,
              border: '1px solid rgba(201, 168, 76, 0.28)',
              borderRadius: '16px',
              padding: isMobile ? '1.2rem' : '1.5rem',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: '0.6875rem',
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: GOLD,
              }}
            >
              Order Summary
            </p>
            <h1
              style={{
                margin: '0.55rem 0 0',
                fontSize: isMobile ? '1.35rem' : '1.6rem',
                fontWeight: 800,
                color: TEXT,
                lineHeight: 1.25,
              }}
            >
              {product.title}
            </h1>
            <p style={{ margin: '0.55rem 0 0', color: MUTED, fontSize: '0.9375rem', lineHeight: 1.55 }}>
              {product.tagline}
            </p>
            <div
              style={{
                marginTop: '1.25rem',
                paddingTop: '1rem',
                borderTop: '1px solid rgba(201, 168, 76, 0.16)',
                display: 'grid',
                gap: '0.55rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: '1rem',
                }}
              >
                <span style={{ color: MUTED, fontSize: '0.875rem', fontWeight: 600 }}>Subtotal</span>
                <span style={{ color: TEXT, fontSize: '1rem', fontWeight: 700 }}>
                  {formatGuidePrice(product.priceUsd)}
                </span>
              </div>
              {discountUsd > 0 ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: '1rem',
                  }}
                >
                  <span style={{ color: GOLD, fontSize: '0.875rem', fontWeight: 700 }}>
                    Promo {appliedPromo}
                  </span>
                  <span style={{ color: GOLD, fontSize: '1rem', fontWeight: 700 }}>
                    -{formatGuidePrice(discountUsd)}
                  </span>
                </div>
              ) : null}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: '1rem',
                  paddingTop: '0.35rem',
                  borderTop: '1px solid rgba(201, 168, 76, 0.12)',
                }}
              >
                <span style={{ color: MUTED, fontSize: '0.875rem', fontWeight: 600 }}>Total due</span>
                <span style={{ color: TEXT, fontSize: '1.75rem', fontWeight: 800 }}>
                  {formatGuidePrice(amountUsd ?? product.priceUsd)}
                </span>
              </div>
            </div>
            {product.savingsUsd ? (
              <p style={{ margin: '0.45rem 0 0', color: GOLD, fontSize: '0.8125rem', fontWeight: 700 }}>
                You save {formatGuidePrice(product.savingsUsd)} with this bundle
              </p>
            ) : null}

            <div style={{ marginTop: '1.15rem' }}>
              <label style={labelStyle} htmlFor="guide-promo-code">
                Promo code
              </label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: appliedPromo ? '1fr auto' : '1fr auto',
                  gap: '0.55rem',
                }}
              >
                <input
                  id="guide-promo-code"
                  type="text"
                  value={appliedPromo || promoInput}
                  onChange={(e) => {
                    setPromoInput(e.target.value);
                    setPromoError('');
                  }}
                  disabled={Boolean(appliedPromo) || promoBusy}
                  placeholder="Enter code"
                  autoComplete="off"
                  style={{
                    ...inputStyle,
                    opacity: appliedPromo ? 0.85 : 1,
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!appliedPromo) handleApplyPromo();
                    }
                  }}
                />
                {appliedPromo ? (
                  <button
                    type="button"
                    onClick={handleRemovePromo}
                    disabled={promoBusy}
                    style={{
                      border: '1px solid rgba(201, 168, 76, 0.45)',
                      backgroundColor: 'transparent',
                      color: GOLD,
                      borderRadius: '0.75rem',
                      padding: '0 1rem',
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      cursor: promoBusy ? 'wait' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Remove
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleApplyPromo}
                    disabled={promoBusy || !promoInput.trim()}
                    style={{
                      border: 'none',
                      backgroundColor: GOLD,
                      color: '#0A0A0A',
                      borderRadius: '0.75rem',
                      padding: '0 1rem',
                      fontWeight: 800,
                      fontSize: '0.8125rem',
                      cursor: promoBusy || !promoInput.trim() ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                      opacity: promoBusy || !promoInput.trim() ? 0.7 : 1,
                    }}
                  >
                    {promoBusy ? '…' : 'Apply'}
                  </button>
                )}
              </div>
              {promoError ? (
                <p role="alert" style={{ margin: '0.45rem 0 0', color: '#fca5a5', fontSize: '0.8125rem' }}>
                  {promoError}
                </p>
              ) : null}
              {appliedPromo && !promoError ? (
                <p style={{ margin: '0.45rem 0 0', color: GOLD, fontSize: '0.8125rem', fontWeight: 700 }}>
                  Promo applied: {formatGuidePrice(discountUsd)} off
                </p>
              ) : null}
            </div>

            <p style={{ margin: '1rem 0 0', color: 'rgba(245,240,232,0.5)', fontSize: '0.8125rem' }}>
              One-time payment. PDF download links are emailed after purchase.
            </p>
          </aside>

          <section
            style={{
              backgroundColor: CARD_BG,
              border: '1px solid rgba(201, 168, 76, 0.22)',
              borderRadius: '16px',
              padding: isMobile ? '1.2rem' : '1.5rem',
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: '0.6875rem',
                fontWeight: 800,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: GOLD,
              }}
            >
              Secure Checkout
            </p>
            <h2
              style={{
                margin: '0.45rem 0 1.15rem',
                fontSize: isMobile ? '1.2rem' : '1.35rem',
                fontWeight: 800,
                color: TEXT,
              }}
            >
              Details & payment
            </h2>

            {loadingIntent ? (
              <p style={{ margin: 0, color: MUTED }}>Loading payment form…</p>
            ) : null}

            {initError ? (
              <p role="alert" style={{ margin: 0, color: '#fca5a5' }}>
                {initError}
              </p>
            ) : null}

            {clientSecret && paymentIntentId ? (
              <Elements
                key={clientSecret}
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: STRIPE_APPEARANCE,
                }}
              >
                <GuidePaymentForm
                  product={product}
                  amountUsd={amountUsd ?? product.priceUsd}
                  fullName={fullName}
                  email={email}
                  setFullName={setFullName}
                  setEmail={setEmail}
                  paymentIntentId={paymentIntentId}
                  clientSecret={clientSecret}
                  onSuccess={handlePaid}
                  isMobile={isMobile}
                />
              </Elements>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
};

export default GuideCheckout;
